import { zodResolver } from "@hookform/resolvers/zod";
import { Copy, Info, Loader2Icon, Plus, X } from "lucide-react";
import {
  useForm,
  SubmitHandler,
  Controller,
  useFieldArray,
  Control,
  UseFormRegister,
  useFormContext,
  useWatch,
} from "react-hook-form";
import * as z from "zod";
import { toast } from "react-toastify";
import { mutate } from "swr";
import { useEffect, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { settings } from "@/settings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import {
  useCreateMeasurementRequest,
  useUpdateMeasurementRequest,
} from "@/hooks/useMeasurementRequests";
import { cn } from "@/lib/utils";
import { TestLabel } from "@/types/testLabel";
import { wafersMap } from "./shared/wafers";
import { Project } from "@/types/project";
import { ChipPos } from "@/types/chipPos";
import { Mask } from "@/types/mask";
import { MeasurementRequest, RawMeasurementRequest } from "@/types/measurementRequest";
import { InfoAlert } from "../shared/Alerts";
import Tooltip from "../shared/Tooltip";

// Constants
const allWafers = [
  { name: "D1", value: 1 },
  { name: "D2", value: 2 },
  { name: "D3", value: 3 },
  { name: "D4", value: 4 },
  { name: "D5", value: 5 },
  { name: "D6", value: 6 },
  { name: "D7", value: 7 },
  { name: "D8", value: 8 },
  { name: "D9", value: 9 },
  { name: "D10", value: 10 },
  { name: "D11", value: 11 },
  { name: "D12", value: 12 },
  { name: "D13", value: 13 },
  { name: "D14", value: 14 },
  { name: "D15", value: 15 },
  { name: "D16", value: 16 },
  { name: "D17", value: 17 },
  { name: "D18", value: 18 },
  { name: "D19", value: 19 },
  { name: "D20", value: 20 },
  { name: "D21", value: 21 },
  { name: "D22", value: 22 },
  { name: "D23", value: 23 },
  { name: "D24", value: 24 },
  { name: "D25", value: 25 },
];

const measurementKeys = [
  "SIPP_UNKNOWN",
  "SIPP_SIMPLEPASSSIDECLAD",
  "SIPP_SIMPLEPASS",
  "SIPP_SIMPLEPASSCLAD",
  "SIPP_ADVPASS",
  "SIPP_ADVPASSCLAD",
  "SIPP_ADVPASSCLAD+SiN",
  "SIPP_M1",
  "SIPP_M2",
  "SIPP_FULLPASS",
  "SIPP_FINAL",
  "SIN_SIMPLEPASS",
  "SIPPN_M1",
  "SIPP_SINSL",
];

// Schemas
const wafersSchema = z
  .array(
    z.object({
      id: z.number(),
      chipPos: z.string().min(1, {
        message: "Field is required.",
      }),
      estimatedTime: z.number(),
      stackShort: z.string(),
      status: z.string(),
      measurementSystemId: z.number().nullable(),
    }),
  )
  .min(1);

const formSchema = z
  .object({
    name: z.string().min(1, {
      message: "Field is required.",
    }),
    projectId: z.number().optional(),
    maskId: z.string().min(2, {
      message: "Field is required.",
    }),
    testLabelId: z.number().optional(),
    imecLotId: z.string().optional(),
    sfabLotId: z.string().optional(),
    physicalLocation: z.string().optional(),
    measurementKey: z.string(),
    targetDate: z.string().min(1, {
      message: "Field is required.",
    }),
    wafers: wafersSchema,
    comment: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.projectId === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Field is required.",
        path: ["projectId"],
      });
    }

    if (!data.imecLotId && !data.sfabLotId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "At least one of 'Imec lot ID' or 'sfab lot ID' must be provided.",
        path: ["imecLotId"],
      });
    }
  });

type WafersSchema = z.infer<typeof wafersSchema>;
type FormSchema = z.infer<typeof formSchema>;

// Utility functions
const getNextValue = (currentValue: number, wafers: WafersSchema, usedWafers: number[]) => {
  const index = allWafers.findIndex((wafer) => wafer.value === currentValue);
  const nextIndex = (index + 1) % allWafers.length;
  const newValue = allWafers[nextIndex].value;

  if (usedWafers.includes(newValue)) {
    return getNextValue(newValue, wafers, usedWafers);
  }

  return newValue;
};

// Types
export type SharedMeasurementRequestDialogProps = {
  chipPos: ChipPos[];
  onOpenChange: (open: boolean) => void;
  open: boolean;
  testLabels: TestLabel[];
};

type DialogMode = "create" | "update" | "testFlight";

interface DialogConfig {
  mode: DialogMode;
  title: string;
  description: string;
  submitLabel: string;
  isNameReadonly?: boolean;
  isMaskDisabled?: boolean;
  isTestLabelDisabled?: boolean;
  isProjectNameDisabled?: boolean;
}

// Components
type WaferProps = {
  chipPos: ChipPos[];
  control: Control<FormSchema>;
  register: UseFormRegister<FormSchema>;
  calculatedMeasTimePerDieMinutes: number;
};

const Wafers = ({ chipPos, control, register, calculatedMeasTimePerDieMinutes }: WaferProps) => {
  const {
    getValues,
    formState: { errors },
  } = useFormContext<FormSchema>();
  const {
    append,
    fields: wafers,
    remove: removeWafer,
  } = useFieldArray({
    control,
    name: `wafers`,
  });

  const formStateWafers = getValues(`wafers`);
  const usedWafers: number[] = formStateWafers.map((w) => w.id);

  const addWafer = () => {
    const currentValue = formStateWafers[wafers.length - 1]?.id;
    const id = currentValue ? getNextValue(currentValue, formStateWafers, usedWafers) : 1;

    append({
      chipPos: "",
      estimatedTime: calculatedMeasTimePerDieMinutes,
      id,
      stackShort: "",
      status: "TO_SCHEDULE",
      measurementSystemId: null,
    });
  };

  const duplicateWafer = (idx: number) => {
    const formStateWafers = getValues("wafers");
    const dataToDuplicate = formStateWafers[idx];
    const lastlyAddedWafer = formStateWafers[formStateWafers.length - 1];
    const usedWafers: number[] = formStateWafers.map((w) => w.id);

    append({
      ...dataToDuplicate,
      id: getNextValue(lastlyAddedWafer.id, formStateWafers, usedWafers),
      // make sure that we set the status to TO_SCHEDULE,
      // it doesn't make sense to duplicate with other statuses
      status: "TO_SCHEDULE",
    });
  };

  return (
    <>
      <div className="space-y-2">
        {errors.wafers?.message ? (
          <FormMessage>You need to link at least one wafer to the test label.</FormMessage>
        ) : null}
        {wafers.length > 0 ? (
          <div
            className="grid gap-3 text-sm font-medium text-muted-foreground"
            style={{
              gridTemplateColumns:
                "100px minmax(150px, 1fr) minmax(180px, 1fr) 160px minmax(120px, 1fr) 80px",
            }}
          >
            <div>Name</div>
            <div>ChipPos</div>
            <div className="flex items-center gap-1">
              Time per Die (min)
              <Tooltip
                asChild={false}
                content={
                  <div className="max-w-xs">
                    <p className="font-semibold mb-1">Auto-calculated from Test Label</p>
                    <p className="text-xs">
                      This value is pre-filled based on the selected test label's estimated
                      measurement time. It's calculated from the total number of port combos across
                      all structures linked to the mask (port combos × 30 seconds).
                    </p>
                  </div>
                }
                trigger={<Info className="h-3 w-3 text-muted-foreground cursor-help" />}
              />
            </div>
            <div>Stack Short</div>
            <div>Actions</div>
          </div>
        ) : null}
        {wafers.map((wafer, idx) => (
          <div
            key={wafer.id}
            className="grid gap-3 items-center"
            style={{
              gridTemplateColumns:
                "100px minmax(150px, 1fr) minmax(180px, 1fr) 160px minmax(120px, 1fr) 80px",
            }}
          >
            <Controller
              control={control}
              name={`wafers.${idx}.id`}
              render={({ field }) => (
                <Select
                  {...field}
                  onValueChange={(val) => field.onChange(val ? Number(val) : undefined)}
                  value={field.value.toString()}
                >
                  <SelectTrigger className="h-8 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(wafersMap).map((key) => (
                      <SelectItem key={key} value={key} disabled={usedWafers.includes(Number(key))}>
                        {wafersMap[Number(key)]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            <div className="w-full">
              <Controller
                control={control}
                name={`wafers.${idx}.chipPos`}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} {...field}>
                    <SelectTrigger className="h-8 w-full">
                      <SelectValue placeholder="Select a ChipPos" />
                    </SelectTrigger>
                    <SelectContent>
                      {chipPos.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.wafers?.[idx]?.chipPos && (
                <FormMessage>{errors.wafers[idx].chipPos.message}</FormMessage>
              )}
            </div>
            <Input
              type="number"
              min={0}
              step={0.1}
              {...register(`wafers.${idx}.estimatedTime`, {
                setValueAs: (value) => (value ? Number(value) : 0),
              })}
              className="h-8 w-full"
            />
            <Input
              {...register(`wafers.${idx}.stackShort`)}
              placeholder="Enter a Stack Short"
              className="h-8 w-full"
            />
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => duplicateWafer(idx)}
                type="button"
              >
                <Copy className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeWafer(idx)}
                className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                type="button"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Button
        disabled={usedWafers.length === Object.keys(wafersMap).length}
        variant="ghost"
        size="sm"
        onClick={addWafer}
        className="text-blue-600 hover:text-blue-800"
        type="button"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Wafer
      </Button>
    </>
  );
};

type BaseMeasurementRequestDialogProps = SharedMeasurementRequestDialogProps & {
  config: DialogConfig;
  defaultValues: FormSchema;
  isExecuting: boolean;
  onSubmit: SubmitHandler<FormSchema>;
  measurementRequestId?: number;
};

const BaseMeasurementRequestDialog = ({
  chipPos,
  config,
  defaultValues,
  isExecuting,
  onOpenChange,
  onSubmit,
  open,
  testLabels,
}: BaseMeasurementRequestDialogProps) => {
  const form = useForm<FormSchema>({
    defaultValues: {
      ...defaultValues,
    },
    resolver: zodResolver(formSchema),
    mode: "all",
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    getFieldState,
    getValues,
    setValue,
  } = form;

  const { maskId, projectId, testLabelId } = useWatch({
    control,
  });

  // Calculate the estimated time per die in minutes from the selected test label
  const calculatedMeasTimePerDieMinutes = useMemo(() => {
    if (!testLabelId) return 0;
    const selectedTestLabel = testLabels.find((tl) => tl.id === testLabelId);

    if (!selectedTestLabel) return 0;
    // Convert from seconds to minutes, rounded to 1 decimal place
    return Math.round((selectedTestLabel.calculatedMeasTimePerDie / 60) * 10) / 10;
  }, [testLabelId, testLabels]);

  // Update all wafer estimated times when test label changes
  // Don't overwrite existing estimated times in edit mode
  useEffect(() => {
    if (config.mode === "update") return;

    const currentWafers = getValues("wafers");
    if (currentWafers && currentWafers.length > 0) {
      currentWafers.forEach((_, index) => {
        setValue(`wafers.${index}.estimatedTime`, calculatedMeasTimePerDieMinutes);
      });
    }
  }, [calculatedMeasTimePerDieMinutes, setValue, getValues, config.mode]);

  const shouldDisableBasicFields =
    getFieldState("projectId").invalid ||
    !getValues("projectId") ||
    getFieldState("name").invalid ||
    !getValues("name");

  const shouldDisableProjectLotAndWaferFields =
    shouldDisableBasicFields ||
    getFieldState("maskId").invalid ||
    !getValues("maskId") ||
    getFieldState("targetDate").invalid ||
    !getValues("targetDate");

  const shouldDisableMaskField =
    getFieldState("name").invalid ||
    !getValues("name") ||
    getFieldState("projectId").invalid ||
    !getValues("projectId");

  const parseStringToInteger = (value: string) => Number(value);

  const chipPosForCurrentlySelectedMask = useMemo(() => {
    if (maskId) {
      return chipPos.filter((c) => c.mask.id === maskId);
    }

    return chipPos;
  }, [chipPos, maskId]);

  const testLabelsForSelectedMask = useMemo(() => {
    if (maskId) {
      return testLabels.filter((label) => label.mask.id === maskId);
    }

    return [];
  }, [maskId, testLabels]);

  const shouldDisableTestLabelField =
    getFieldState("name").invalid || !getValues("name") || testLabelsForSelectedMask.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] md:max-w-[80%] overflow-y-auto lg:w-[90%] lg:max-w-[1400px] mx-auto">
        <DialogHeader>
          <DialogTitle>{config.title}</DialogTitle>
        </DialogHeader>
        <DialogDescription>{config.description}</DialogDescription>
        <Form {...form}>
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {/* General Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">General</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormItem>
                  <FormLabel htmlFor="name">
                    Request name <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      id="name"
                      placeholder="Enter a Measurement Request name"
                      disabled={config.isNameReadonly}
                      {...register("name")}
                    />
                  </FormControl>
                  {errors.name && <FormMessage>{errors.name.message}</FormMessage>}
                </FormItem>

                <FormItem>
                  <FormLabel htmlFor="testLabelId">
                    Test Label <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Controller
                      control={control}
                      name="testLabelId"
                      render={({ field }) => (
                        <Select
                          disabled={shouldDisableTestLabelField || config.isTestLabelDisabled}
                          onValueChange={(value) => field.onChange(parseStringToInteger(value))}
                          value={field.value?.toString()}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue
                              placeholder={
                                !projectId
                                  ? "Select a project first"
                                  : !maskId
                                    ? "Select a mask first"
                                    : testLabelsForSelectedMask.length === 0
                                      ? "No Test Labels available for this project"
                                      : "Select a Test Label"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {testLabelsForSelectedMask.map((tl) => (
                              <SelectItem
                                key={tl.id}
                                value={tl.id.toString()}
                                className={tl.status === "NOT_VALIDATED" ? "text-gray-400" : ""}
                              >
                                {tl.name}
                                {tl.status === "NOT_VALIDATED" && (
                                  <span className="text-gray-400 ml-1">(not validated)</span>
                                )}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </FormControl>
                  {errors.maskId && <FormMessage>{errors.maskId.message}</FormMessage>}
                </FormItem>

                <FormItem>
                  <FormLabel htmlFor="targetDate">
                    Target Date<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      id="targetDate"
                      type="date"
                      disabled={shouldDisableMaskField}
                      {...register("targetDate")}
                    />
                  </FormControl>
                  {errors.targetDate && <FormMessage>{errors.targetDate.message}</FormMessage>}
                </FormItem>
              </CardContent>
            </Card>

            {/* Lot Info Section */}
            <Card className={cn(shouldDisableProjectLotAndWaferFields ? "opacity-30" : "")}>
              <CardHeader>
                <CardTitle className="text-lg">Lot info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {errors.imecLotId ? null : (
                  <InfoAlert>
                    At least one of 'Imec lot ID' or 'sfab lot ID' must be provided.
                  </InfoAlert>
                )}
                <FormItem>
                  <FormLabel htmlFor="imecLotId">Imec Lot ID</FormLabel>
                  <FormControl>
                    <Input
                      id="imecLotId"
                      disabled={shouldDisableProjectLotAndWaferFields}
                      placeholder="Enter an Imec Lot ID"
                      {...register("imecLotId")}
                    />
                  </FormControl>
                  {errors.imecLotId && <FormMessage>{errors.imecLotId.message}</FormMessage>}
                </FormItem>
                <FormItem>
                  <FormLabel htmlFor="sfabLotId">sfab Lot ID</FormLabel>
                  <FormControl>
                    <Input
                      id="sfabLotId"
                      disabled={shouldDisableProjectLotAndWaferFields}
                      placeholder="sfab Lot ID"
                      {...register("sfabLotId")}
                    />
                  </FormControl>
                </FormItem>
                <FormItem>
                  <FormLabel htmlFor="physicalLocation">Physical location</FormLabel>
                  <FormControl>
                    <Input
                      id="physicalLocation"
                      disabled={shouldDisableProjectLotAndWaferFields}
                      placeholder="Physical location"
                      {...register("physicalLocation")}
                    />
                  </FormControl>
                </FormItem>

                <FormItem>
                  <FormLabel htmlFor="measurementKey">Measurement Key</FormLabel>
                  <FormControl>
                    <Controller
                      control={control}
                      name="measurementKey"
                      render={({ field }) => (
                        <Select
                          disabled={shouldDisableProjectLotAndWaferFields}
                          onValueChange={field.onChange}
                          {...field}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select a Measurement Key" />
                          </SelectTrigger>
                          <SelectContent>
                            {measurementKeys.map((keys) => (
                              <SelectItem key={keys} value={keys}>
                                {keys}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </FormControl>
                </FormItem>
              </CardContent>
            </Card>

            {/* Wafers Section */}
            <Card className={shouldDisableProjectLotAndWaferFields ? "opacity-30" : ""}>
              <CardHeader>
                <CardTitle className="text-lg">Wafers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <Wafers
                  chipPos={chipPosForCurrentlySelectedMask}
                  control={control}
                  register={register}
                  calculatedMeasTimePerDieMinutes={calculatedMeasTimePerDieMinutes}
                />
              </CardContent>
            </Card>

            {/* Initial Comment Section - Only show for create mode */}
            {config.mode === "create" && (
              <Card className={shouldDisableProjectLotAndWaferFields ? "opacity-30" : ""}>
                <CardHeader>
                  <CardTitle className="text-lg">Additional Comment</CardTitle>
                </CardHeader>
                <CardContent>
                  <FormItem>
                    <FormLabel htmlFor="comment">
                      Add a comment or special request to this measurement request
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        id="comment"
                        disabled={shouldDisableProjectLotAndWaferFields}
                        placeholder="Enter your comment here..."
                        className="min-h-[100px]"
                        {...register("comment")}
                      />
                    </FormControl>
                  </FormItem>
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(!open)}
                type="button"
                disabled={isExecuting}
              >
                Cancel
              </Button>
              <Button disabled={isExecuting || shouldDisableProjectLotAndWaferFields} type="submit">
                {isExecuting && <Loader2Icon className="animate-spin mr-2" />}
                {config.submitLabel}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

// Higher-level wrapper components
type CreateMeasurementRequestDialogProps = SharedMeasurementRequestDialogProps & {
  maskId: string;
  project: Project;
};

export const CreateMeasurementRequestDialog = ({
  chipPos,
  maskId,
  onOpenChange,
  open,
  project,
  testLabels,
}: CreateMeasurementRequestDialogProps) => {
  const { trigger: createMeasurementRequest, isMutating: isCreatingMeasurementRequest } =
    useCreateMeasurementRequest();
  const { accessToken } = useAuth();

  const defaultValues: FormSchema = {
    comment: undefined,
    imecLotId: undefined,
    maskId,
    measurementKey: "",
    name: "",
    physicalLocation: undefined,
    projectId: project.id,
    sfabLotId: undefined,
    targetDate: "",
    testLabelId: undefined,
    wafers: [],
  };

  const config: DialogConfig = {
    mode: "create",
    title: "New Measurement Request",
    description: "Create a new Measurement Request",
    submitLabel: "Create",
    isMaskDisabled: true,
    isProjectNameDisabled: true,
  };

  const onSubmit: SubmitHandler<FormSchema> = async (data) => {
    try {
      await createMeasurementRequest(
        {
          imec_lot_id: data.imecLotId ?? null,
          mask_id: data.maskId,
          measurement_key: data.measurementKey ? data.measurementKey : measurementKeys[0],
          physical_location: data.physicalLocation ?? null,
          project_id: data.projectId!,
          sfab_lot_id: data.sfabLotId ?? null,
          target_date: data.targetDate ?? null,
          name: data.name,
          test_label_id: data.testLabelId!,
          wafer_requests: data.wafers.map((w) => ({
            id: w.id,
            chip_pos_id: w.chipPos,
            stack_short: w.stackShort,
            estimated_time: Math.round(w.estimatedTime * 60),
            status: w.status,
            measurement_system_id: w.measurementSystemId,
          })),
        },
        {
          onError: (err) => toast.error(`Error: ${err.message}`),
          onSuccess: async (createdMeasurementRequest: RawMeasurementRequest) => {
            // If a comment was provided, create it after the measurement request is successfully created
            if (data.comment && data.comment.trim() && createdMeasurementRequest?.id) {
              try {
                const commentResponse = await fetch(
                  `${settings.apiBaseUrl}/measurement_requests/${createdMeasurementRequest.id}/comments`,
                  {
                    method: "POST",
                    headers: {
                      Authorization: `Bearer ${accessToken}`,
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ comment: data.comment }),
                  },
                );

                if (!commentResponse.ok) {
                  throw new Error("Failed to create comment");
                }
              } catch (commentErr) {
                console.error("Failed to create comment:", commentErr);
                toast.warning(
                  "Measurement Request created, but comment failed to save. Try to add it again.",
                );
              }
            }

            // Show success message and refresh
            toast.success(`Successfully created Measurement Request: "${data.name}"!`);
            mutate({ url: `/measurement_requests/mask/${maskId}` });
            onOpenChange(!open);
          },
        },
      );
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <BaseMeasurementRequestDialog
      chipPos={chipPos}
      config={config}
      defaultValues={defaultValues}
      isExecuting={isCreatingMeasurementRequest}
      onOpenChange={onOpenChange}
      onSubmit={onSubmit}
      open={open}
      testLabels={testLabels.filter((label) => !label.isArchived)}
    />
  );
};

type UpdateMeasurementRequestDialogProps = SharedMeasurementRequestDialogProps & {
  measurementRequest: MeasurementRequest;
};

export const UpdateMeasurementRequestDialog = ({
  chipPos,
  measurementRequest,
  onOpenChange,
  open,
  testLabels,
}: UpdateMeasurementRequestDialogProps) => {
  const { trigger: updateMeasurementRequest, isMutating: isUpdatingMeasurementRequest } =
    useUpdateMeasurementRequest();

  const defaultValues: FormSchema = {
    imecLotId: measurementRequest.imecLotId ?? "",
    maskId: measurementRequest.mask.id,
    measurementKey: measurementRequest.measurementKey,
    physicalLocation: measurementRequest.physicalLocation ?? "",
    projectId: measurementRequest.project.id,
    name: measurementRequest.name,
    sfabLotId: measurementRequest.sfabLotId ?? "",
    targetDate: measurementRequest.targetDate
      ? new Date(measurementRequest.targetDate).toISOString().split("T")[0]
      : "",
    wafers: measurementRequest.waferRequests,
    testLabelId: measurementRequest.testLabel.id,
  };

  const config: DialogConfig = {
    mode: "update",
    title: "Update Measurement Request",
    description: "Update this Measurement Request",
    submitLabel: "Update",
  };

  const onSubmit: SubmitHandler<FormSchema> = async (data) => {
    try {
      await updateMeasurementRequest(
        {
          id: measurementRequest.id.toString(),
          imec_lot_id: data.imecLotId ?? null,
          mask_id: data.maskId,
          measurement_key: data.measurementKey ? data.measurementKey : measurementKeys[0],
          physical_location: data.physicalLocation ?? null,
          project_id: data.projectId,
          sfab_lot_id: data.sfabLotId ?? null,
          status: measurementRequest.status,
          target_date: data.targetDate ?? null,
          name: data.name,
          test_label_id: data.testLabelId!,
          wafer_requests: data.wafers.map((w) => ({
            id: w.id,
            chip_pos_id: w.chipPos,
            stack_short: w.stackShort,
            estimated_time: Math.round(w.estimatedTime * 60),
            status: w.status,
            measurement_system_id: w.measurementSystemId,
          })),
        },
        {
          onError: (err) => toast.error(`Error: ${err.message}`),
          onSuccess: () => {
            toast.success(`Successfully updated Measurement Request: "${data.name}"!`);
            mutate({ url: "/measurement_requests" });
            mutate({ url: `/measurement_requests/${measurementRequest.id}` });
            onOpenChange(!open);
          },
        },
      );
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <BaseMeasurementRequestDialog
      chipPos={chipPos}
      config={config}
      defaultValues={defaultValues}
      isExecuting={isUpdatingMeasurementRequest}
      onOpenChange={onOpenChange}
      onSubmit={onSubmit}
      open={open}
      testLabels={testLabels}
      measurementRequestId={measurementRequest.id}
    />
  );
};

type CreateTestFlightRequestDialogProps = SharedMeasurementRequestDialogProps & {
  mask: Mask;
  testLabel: TestLabel;
  project: Project;
};

export const CreateTestFlightRequestDialog = ({
  chipPos,
  mask,
  onOpenChange,
  open,
  project,
  testLabel,
  testLabels,
}: CreateTestFlightRequestDialogProps) => {
  const { trigger: createTestFlightRequest, isMutating: isCreatingTestFlightRequest } =
    useCreateMeasurementRequest();

  const defaultValues: FormSchema = {
    imecLotId: undefined,
    maskId: mask.id,
    measurementKey: "",
    physicalLocation: undefined,
    projectId: project.id,
    name: `[test-flight] ${testLabel.name}`,
    sfabLotId: undefined,
    testLabelId: testLabel.id,
    targetDate: "",
    wafers: [],
  };

  const config: DialogConfig = {
    mode: "testFlight",
    title: "New Test Flight Request",
    description: "Create a new Test Flight Request",
    submitLabel: "Create Test Flight",
    isNameReadonly: true,
    isMaskDisabled: true,
    isTestLabelDisabled: true,
    isProjectNameDisabled: true,
  };

  const onSubmit: SubmitHandler<FormSchema> = async (data) => {
    try {
      await createTestFlightRequest(
        {
          imec_lot_id: data.imecLotId ?? null,
          mask_id: data.maskId,
          measurement_key: data.measurementKey ? data.measurementKey : measurementKeys[0],
          physical_location: data.physicalLocation ?? null,
          project_id: data.projectId!,
          sfab_lot_id: data.sfabLotId ?? null,
          target_date: data.targetDate ?? null,
          name: data.name,
          test_label_id: data.testLabelId!,
          wafer_requests: data.wafers.map((w) => ({
            id: w.id,
            chip_pos_id: w.chipPos,
            stack_short: w.stackShort,
            estimated_time: Math.round(w.estimatedTime * 60),
            status: w.status,
            measurement_system_id: w.measurementSystemId,
          })),
        },
        {
          onError: (err) => toast.error(`Error: ${err.message}`),
          onSuccess: () => {
            toast.success(`Successfully created Test Flight Request: "${data.name}"!`);
            mutate({ url: "/measurement_requests" });
            onOpenChange(!open);
          },
        },
      );
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <BaseMeasurementRequestDialog
      chipPos={chipPos}
      config={config}
      defaultValues={defaultValues}
      isExecuting={isCreatingTestFlightRequest}
      onOpenChange={onOpenChange}
      onSubmit={onSubmit}
      open={open}
      testLabels={testLabels}
    />
  );
};
